import {
  ChakraFlex,
  FAIconName,
  FireflyButton,
  ModalV2,
  Text,
} from '@equip.health/ui';
import { useState } from 'react';
import { CarePlanEvents } from '~/analytics/carePlanEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CarePlanStatus } from '~/lib/types/carePlan/CarePlanStatus';
import { useUpdateCarePlanMutation } from '~/store/services/carePlan';
import { isDraftNotesConflictError } from '~/lib/util/carePlan.util';
import { DraftNotesConflictModal } from './DraftNotesConflictModal';

const TOP_LEVEL_ID = 'void-care-plan-modal';
type Props = {
  treatmentEpisodeId: string;
  treatmentPathwayId: string;
  onClose: () => void;
};
export const VoidCarePlanModal = ({
  onClose,
  treatmentEpisodeId,
  treatmentPathwayId,
}: Props) => {
  const { trackEvent } = useAnalytics();
  const [updateCarePlan, { isLoading: isUpdateCarePlanLoading }] =
    useUpdateCarePlanMutation();
  const [isDraftConflictModalOpen, setIsDraftConflictModalOpen] =
    useState(false);

  return (
    <ModalV2
      isOpen
      onClose={() => {
        trackEvent(CarePlanEvents.clickOnCloseIconInVoidPlanModal);
        onClose();
      }}
      title="Void current care plan"
    >
      <Text>
        Are you sure you want to close this care plan? Once the plan is closed,
        it can’t be reopened again.
      </Text>
      <ChakraFlex
        alignItems="center"
        justifyContent="space-between"
        marginTop="24px"
        width="100%"
      >
        <FireflyButton
          id={`${TOP_LEVEL_ID}-cancel-button`}
          isLoading={isUpdateCarePlanLoading}
          leftIcon={FAIconName.timesRegular}
          onClick={() => {
            trackEvent(CarePlanEvents.clickOnCancelButtonInVoidPlanModal);
            onClose();
          }}
          variant="secondary"
        >
          Cancel
        </FireflyButton>
        <FireflyButton
          id={`${TOP_LEVEL_ID}-void-button`}
          isLoading={isUpdateCarePlanLoading}
          leftIcon={FAIconName.trashRegular}
          onClick={async () => {
            trackEvent(CarePlanEvents.clickOnVoidCarePlanButtonInVoidPlanModal);
            try {
              await updateCarePlan({
                treatmentEpisodeId,
                carePlanId: treatmentPathwayId,
                statusCode: CarePlanStatus.VOID,
              }).unwrap();
              onClose();
            } catch (error) {
              if (isDraftNotesConflictError(error)) {
                setIsDraftConflictModalOpen(true);
              }
            }
          }}
          variant="accent.critical"
        >
          Void care plan
        </FireflyButton>
      </ChakraFlex>
      <DraftNotesConflictModal
        isOpen={isDraftConflictModalOpen}
        onClose={() => setIsDraftConflictModalOpen(false)}
        onCloseParent={onClose}
        operation="void"
      />
    </ModalV2>
  );
};
